import React from 'react';
import { connect } from 'react-redux';
import * as VendorEnrollmentRequestViewActions from '../actions/VendorEnrollmentRequestView';
import Request from '../components/Request';
import EnrollmentConfirmation from '../components/EnrollmentConfirmation';
import * as constants from 'constants/App';
import { renderError } from 'utils/HelperFunctions';
import VendorEnrollmentRequestViewSelector from '../selectors/VendorEnrollmentRequestView';
import { fromJS } from 'immutable';
import { defineMessages, injectIntl } from 'react-intl';
import configuration from 'configuration';
import { jwtDecode } from 'jwt-decode';
import { useLocation } from 'react-router-dom';
import NavigationPrompt from 'shared/components/NavigationPrompt';

const VendorEnrollmentRequestView = (props) => {
  const location = useLocation();

  React.useEffect(() => {
    //clear out the state for both request and enrollment
    props.dispatch(VendorEnrollmentRequestViewActions.resetForm());

    // Prefill from token into Redux so submit captures disabled field values
    try {
      const search = location.search || (typeof window !== 'undefined' ? window.location.search : '');
      if (search) {
        const searchParams = new URLSearchParams(search);
        let token = searchParams.get('token');

        if (token && configuration.singleUseEnrollmentCodeEnabled) {
          const decoded = jwtDecode(token);
          const details =
            decoded && decoded.enrollmentLinkDetails ? decoded.enrollmentLinkDetails : {};

          const phone = details.vendorPhone || '';
          let area = '',
            prefix = '',
            suffix = '';
          if (phone && phone.length === 10) {
            area = phone.substring(0, 3);
            prefix = phone.substring(3, 6);
            suffix = phone.substring(6, 10);
          }

          const F = constants.VENDOR_ENROLLMENT_REQUEST_FIELD_NAME;
          props.dispatch(
            VendorEnrollmentRequestViewActions.addRequestInfo({
              name: F.ENROLLMENT_LINK_TOKEN,
              value: token,
            })
          )
          if (details.enrollmentLinkId) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.ENROLLMENT_LINK_ID,
                value: details.enrollmentLinkId,
              })
            );
          }
          if (details.vendorName) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.BUSINESS_NAME,
                value: details.vendorName,
              })
            );
          }
          if (details.countryCode) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.COUNTRY,
                value: details.countryCode,
              })
            );
          }
          if (details.contactName) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.CONTACT_NAME,
                value: details.contactName,
              })
            );
          }
          if (details.vendorEmail) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.EMAIL,
                value: details.vendorEmail,
              })
            );
          }
          if (area) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({ name: F.PHONE_AREA, value: area })
            );
          }
          if (prefix) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.PHONE_PREFIX,
                value: prefix,
              })
            );
          }
          if (suffix) {
            props.dispatch(
              VendorEnrollmentRequestViewActions.addRequestInfo({
                name: F.PHONE_SUFFIX,
                value: suffix,
              })
            );
          }
        }
      }
    } catch (err) {
      // Non-fatal: ignore invalid tokens
    }
  }, [props.dispatch, location.search]);

  const onRequestInfoChangeHandler = (inputName, event) => {
    let requestInfo = {
      name: inputName,
      value: event.target.value,
    };
    props.dispatch(VendorEnrollmentRequestViewActions.addRequestInfo(requestInfo));
    props.dispatch(VendorEnrollmentRequestViewActions.formIsDirty());
  };

  const scrollToTop = () => {
    if (
      props.enrollmentRequestErrorMessage != undefined &&
      typeof props.enrollmentRequestErrorMessage != 'string' &&
      props.enrollmentRequestErrorMessage.length > 0
    ) {
      window.scrollTo(0, 0);
    }
  };

  const onSubmitRequestHandler = () => {
    if (
      !configuration.singleUseEnrollmentCodeEnabled &&
      typeof window !== 'undefined' &&
      typeof window.grecaptcha !== 'undefined' &&
      typeof window.grecaptcha.render === 'function'
    ) {
      let requestInfo = {
        name: constants.VENDOR_ENROLLMENT_REQUEST_FIELD_NAME.RECAPTCHA_VERIFICATION_TOKEN,
        value: grecaptcha.getResponse() != undefined ? grecaptcha.getResponse() : '',
      };

      props.dispatch(VendorEnrollmentRequestViewActions.addRequestInfo(requestInfo));

      if (requestInfo.value !== '') {
        props.dispatch(VendorEnrollmentRequestViewActions.submitRequestInfo());
        setTimeout(scrollToTop, 250);
      }
    } else {
      props.dispatch(VendorEnrollmentRequestViewActions.submitRequestInfo());
      setTimeout(scrollToTop, 250);
    }
  };

  const messages = defineMessages({
      unsaved_data: {
        id: 'unsaved_data',
        description: 'unsaved_data',
        defaultMessage:
          'You have unsaved changes that will be lost if you decide to continue. Click OK to continue or Cancel to stay on the same page.',
      },
    });
  const { formatMessage } = props.intl;

  return (
    <div>
       <NavigationPrompt
            when={!props.isSubmitting && props.isDirty}
                message={formatMessage(messages.unsaved_data)}
            />
      {
        //render server error
        renderError('genericErrorMessage', props, props.genericErrorMessage)
      }
      {!props.requestSubmitted ? (
        <Request
          enrollmentRequestInfo={props.enrollmentRequestInfo}
          onRequestInfoChange={onRequestInfoChangeHandler}
          usStates={toArray(props.usStates)}
          onSubmit={onSubmitRequestHandler}
          isRequestFormValid={props.isRequestFormValid}
          requestSubmitted={props.requestSubmitted}
          error={renderError(
            'enrollmentRequestErrorMessage',
            props,
            props.enrollmentRequestErrorMessage
          )}
          canadianProvinces={toArray(props.canadianProvinces)}
          reCaptchaKey={configuration.reCaptchaKey}
          reCaptchaVerificationToken={props.reCaptchaVerificationToken}
          isRequestSubmitting={props.isSubmitting}
        />
      ) : (
        <EnrollmentConfirmation />
      )}
    </div>
  );
};

const toArray = (value) => {
  if (value == undefined || value == null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value.toJS === 'function') {
    const jsValue = value.toJS();
    return Array.isArray(jsValue) ? jsValue : Object.values(jsValue || {});
  }
  if (typeof value.toJSON === 'function') {
    const jsonValue = value.toJSON();
    return Array.isArray(jsonValue) ? jsonValue : Object.values(jsonValue || {});
  }
  if (typeof value === 'object') return Object.values(value);
  return [];
};

function mapStateToProps(state, props) {
  return {
    ...VendorEnrollmentRequestViewSelector(state, props),
    enrollmentRequestInfo: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'enrollmentRequestInfo',
    ]),
    usStates: fromJS(state.appState.getIn(['uiData', 'shared', 'usStates']) || []),
    enrollmentStep: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'enrollmentStep',
    ]),
    confirmationMessage: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'confirmationMessage',
    ]),
    termsAndConditions: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'enrollmentTermsAndConditions',
    ]),
    federalTaxClassifications: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'federalTaxClassifications',
    ]),
    liabilityTaxClassification: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'liabilityTaxClassification',
    ]),
    requestSubmitted: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'requestSubmitted',
    ]),
    enrollmentRequestErrorMessage: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'errorMessage',
    ]),
    genericErrorMessage: state.appState.getIn(['uiData', 'shared', 'errorMessage']),
    isDirty: state.appState.getIn(['uiData', 'vendorEnrollmentRequestView', 'isDirty']),
    isSubmitting: state.appState.getIn(['uiData', 'vendorEnrollmentRequestView', 'isSubmitting']),
    canadianProvinces: fromJS(state.appState.getIn(['uiData', 'shared', 'canadianProvinces']) || []),
    reCaptchaVerificationToken: state.appState.getIn([
      'uiData',
      'vendorEnrollmentRequestView',
      'enrollmentRequestInfo',
      'reCaptchaVerificationToken',
    ]),
  };
}

export default connect(mapStateToProps)(injectIntl(VendorEnrollmentRequestView));
